import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { css } from '@emotion/core';
import { Grid, GridElement } from '../../design-system/Grid';
import { ResponsiveImage } from '../../design-system/Images';
import Paragraph from '../../design-system/Paragraph';
import { Button, LinkButton } from '../../design-system/Buttons';
import DashboardButton from '../../pages/Dashboard/DashboardButton';
import AccordionGroup from '../Accordion';
import phrasesType from '../../proptypes/phrases';
import formType from '../../proptypes/forms';
import ProfileInvitationModal from './ProfileInvitationModal';

export const visitGroupType = PropTypes.arrayOf(
  PropTypes.exact({
    id: PropTypes.string.isRequired,
    fullName: PropTypes.string,
    scanCode: PropTypes.string.isRequired,
    profilePath: PropTypes.string.isRequired,
    dossierPath: PropTypes.string.isRequired,
    archetypeId: PropTypes.string,
    premium: PropTypes.bool.isRequired,
    allowInvite: PropTypes.bool.isRequired,
    email: PropTypes.string,
  }),
);

const GroupVisits = ({
  group,
  onUnlockProfileClick,
  onDownloadDossierClick,
  showPremiumOptions,
  phrases,
  profileInviteForm,
}) => {
  const [profileInvitationData, setProfileInvitationData] = useState(null);

  const handleOnProfileInvitationCloseClick = () => {
    setProfileInvitationData(null);
  };

  const showProfileInvitationModal = Object.keys(profileInvitationData || {})
    .length;

  return (
    <>
      {group.map(
        (
          {
            fullName: rawFullName,
            id,
            profilePath,
            scanCode,
            premium,
            dossierPath,
            email,
            allowInvite,
            archetypeId,
          },
          index,
        ) => {
          const fullName = rawFullName || phrases.groupVisitor.noNameFallback;

          const isFirstItem = index === 0;
          const isLastItem = index === group.length;

          const showNoArchetypeInviteMessage = showPremiumOptions && allowInvite && !archetypeId;

          const handleOnUnlockProfileClick = () => {
            onUnlockProfileClick([{ fullName, id }]);
          };

          const handleOnProfileInvitationClick = () => {
            setProfileInvitationData({
              fullName,
              scanCode,
              profileId: id,
              email,
            });
          };

          return (
            <React.Fragment key={id}>
              <AccordionGroup
                isActiveByDefault={isFirstItem}
                id={id}
                controlContent={(
                  <Grid
                    pxXs
                    pyXs
                    borderT={!isFirstItem}
                    borderB={isLastItem}
                    w100
                    key={id}
                  >
                    <GridElement center>
                      <ResponsiveImage
                        alt=""
                        src="//images.ctfassets.net/gtqjh9j40j4m/gcfaNCHtR6OAXGfAV0YkU/7bcd9cd4587d1c5eeb4b4187b88cd3fd/ticket-icon.svg"
                        css={css`
                          width: 45px;
`}
                      />
                    </GridElement>
                    <GridElement
                      key={id}
                      center
                      css={css`
                        flex: 1 1 100%;
`}
                    >
                      <Grid w100 extent>
                        <GridElement pxSm pyXs>
                          <Paragraph
                            my0
                            intro
                            tAlignL
                            className="ss_tt--uppercase"
                          >
                            {fullName}
                          </Paragraph>
                          <Paragraph
                            my0
                            sub
                            tAlignL
                            className="ss_tt--uppercase ss_color--secondary"
                          >
                            Ticket:
                            {scanCode}
                          </Paragraph>
                        </GridElement>
                      </Grid>
                    </GridElement>
                  </Grid>
                )}
                bodyContent={(
                  <>
                    {showNoArchetypeInviteMessage && (
                      <Paragraph tAlignL my0 pxXs className="ss_color--warn">
                        {phrases.profileInvite.noArchetypeMessage}
                      </Paragraph>
                    )}
                    <Grid smVertical pbXs w100 equalWidth>
                      {!showPremiumOptions && (
                        <GridElement pxXs>
                          <LinkButton
                            href={profilePath}
                            secondary
                            w100
                            pyXs
                            myXs
                          >
                            {phrases.groupVisitor.viewProfile}
                          </LinkButton>
                        </GridElement>
                      )}
                      {showPremiumOptions && (
                        <>
                          {allowInvite && (
                            <GridElement pxXs my0>
                              <Button
                                onClick={() => handleOnProfileInvitationClick()}
                                secondary
                                w100
                                pyXs
                                myXs
                                my0
                              >
                                {phrases.groupVisitor.inviteToProfile}
                              </Button>
                            </GridElement>
                          )}
                          <GridElement pxXs my0>
                            <DashboardButton
                              onClickLocked={handleOnUnlockProfileClick}
                              onClickUnlocked={onDownloadDossierClick}
                              locked={!premium}
                              href={dossierPath}
                              secondary
                              w100
                              pyXs
                              myXs
                              my0
                            >
                              {premium
                                ? phrases.groupVisitor.downloadDossier
                                : phrases.groupVisitor.unlockProfile}
                            </DashboardButton>
                          </GridElement>
                        </>
                      )}
                    </Grid>
                  </>
                )}
              />
            </React.Fragment>
          );
        },
      )}
      {showProfileInvitationModal && (
        <ProfileInvitationModal
          visible
          form={profileInviteForm}
          phrases={phrases.profileInvite}
          onCloseClick={handleOnProfileInvitationCloseClick}
          inputFieldValues={profileInvitationData}
        />
      )}
    </>
  );
};

GroupVisits.propTypes = {
  group: visitGroupType.isRequired,
  onUnlockProfileClick: PropTypes.func.isRequired,
  onDownloadDossierClick: PropTypes.func.isRequired,
  showPremiumOptions: PropTypes.bool.isRequired,
  phrases: phrasesType.isRequired,
  profileInviteForm: formType.isRequired,
};

export default GroupVisits;
